import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import AdminNavBar from "../components/AdminNavbar";
import "../assets/css/viewstudents.css";

const API =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_BASE_URL || "https://schools.luth.gov.ng"
    : "http://localhost:3003";

const PROGRAMME_OPTIONS = [
  "Community Health II",
  "Community Health I",
  "Accident and Emergency",
  "Critical Care",
  "Dental Nursing",
  "Neonatal Nursing",
  "Nephrology Nursing",
  "Nursing Education",
  "Oncology Nursing",
  "Ophthalmic Nursing",
  "Paediatric Nursing",
  "Perioperative Nursing",
  "Federal School of Biomedical Engineering",
  "General Nursing",
  "Health Information Management",
  "Medical and Psychiatry Social Work",
];

const safeText = (value, fallback = "N/A") =>
  value === null || value === undefined || String(value).trim() === ""
    ? fallback
    : value;

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return safeText(value);
  return dt.toLocaleString();
};

const normalizeProgramme = (value) => {
  const text = safeText(value, "");
  const found = PROGRAMME_OPTIONS.find(
    (p) => p.toLowerCase() === String(text).toLowerCase(),
  );
  return found || text;
};

export default function AdminViewResultsPage() {
  const [results, setResults] = useState([]);
  const [programmeCourses, setProgrammeCourses] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState("All");
  const [selectedLevel, setSelectedLevel] = useState("All");
  const [selectedCourse, setSelectedCourse] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [pageError, setPageError] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    let cancelled = false;

    const loadProgrammeCourses = async () => {
      if (!selectedProgramme || selectedProgramme === "All") {
        setProgrammeCourses([]);
        setSelectedLevel("All");
        setSelectedCourse("All");
        return;
      }

      try {
        setLoadingCourses(true);

        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/api/admin/results-courses`, {
          headers,
          params: {
            programme: selectedProgramme,
          },
        });

        const rows = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) {
          setProgrammeCourses(rows);
          setSelectedLevel("All");
          setSelectedCourse("All");
        }
      } catch (err) {
        console.error("Failed to load courses:", err);
        if (!cancelled) {
          setProgrammeCourses([]);
          setSelectedLevel("All");
          setSelectedCourse("All");
        }
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    };

    loadProgrammeCourses();

    return () => {
      cancelled = true;
    };
  }, [selectedProgramme, token]);

  useEffect(() => {
    let cancelled = false;

    const loadResults = async () => {
      try {
        setLoading(true);
        setPageError("");

        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/api/students-results-view`, {
          headers,
          params: {
            programme: selectedProgramme,
            level: selectedLevel,
            courseId: selectedCourse,
          },
        });

        const rows = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setResults(rows);
      } catch (err) {
        console.error("Failed to load results:", err);
        if (!cancelled) {
          setResults([]);
          setPageError("Some result data could not be loaded.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadResults();

    return () => {
      cancelled = true;
    };
  }, [selectedProgramme, selectedLevel, selectedCourse, token]);

  const normalizedResults = useMemo(() => {
    return results.map((r) => ({
      ...r,
      fullName: r.FullName || r.fullName || r.StudentName || "",
      matricNumber: r.MatricNumber || r.matricNumber || "",
      programme: normalizeProgramme(r.Programme || r.programme || r.department),
      level: r.Level || r.level || r.StudentLevel || "",
      session: r.Session || r.session || "",
      semester: r.Semester || r.semester || "",
      courseId: r.CourseID || r.courseId || "",
      courseCode: r.CourseCode || r.courseCode || "",
      courseTitle: r.CourseTitle || r.courseTitle || "",
      score: r.Score ?? r.score ?? r.ExistingScore ?? "",
      grade: r.Grade ?? r.grade ?? r.ExistingGrade ?? "",
      status: r.Status ?? r.status ?? r.ResultStatus ?? "Provisional",
      enteredBy: r.EnteredBy || r.enteredBy || "",
      enteredAt: r.EnteredAt || r.enteredAt || "",
    }));
  }, [results]);

  const filteredResults = useMemo(() => {
    const q = search.trim().toLowerCase();

    return normalizedResults.filter((r) => {
      const programmeMatch =
        selectedProgramme === "All" ||
        normalizeText(r.programme) === normalizeText(selectedProgramme);

      const levelMatch =
        selectedLevel === "All" ||
        normalizeText(r.level) === normalizeText(selectedLevel);

      const courseMatch =
        selectedCourse === "All" ||
        String(r.courseId || "") === String(selectedCourse);

      const searchMatch =
        !q ||
        normalizeText(r.fullName).includes(q) ||
        normalizeText(r.matricNumber).includes(q) ||
        normalizeText(r.courseCode).includes(q) ||
        normalizeText(r.courseTitle).includes(q);

      return programmeMatch && levelMatch && courseMatch && searchMatch;
    });
  }, [
    normalizedResults,
    selectedProgramme,
    selectedLevel,
    selectedCourse,
    search,
  ]);

  const totalResults = filteredResults.length;
  const approvedCount = filteredResults.filter(
    (r) => normalizeText(r.status) === "approved",
  ).length;
  const provisionalCount = filteredResults.filter(
    (r) => normalizeText(r.status) !== "approved",
  ).length;
  const averageScore =
    totalResults > 0
      ? (
          filteredResults.reduce((sum, r) => sum + (Number(r.score) || 0), 0) /
          totalResults
        ).toFixed(2)
      : "0.00";

  const availableLevels = useMemo(() => {
    const levels = [
      ...new Set(
        programmeCourses
          .map((c) => String(c.Level || c.level || "").trim())
          .filter(Boolean),
      ),
    ];
    return levels.sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [programmeCourses]);

  const availableCourses = useMemo(() => {
    return programmeCourses.filter((course) => {
      const levelValue = String(course.Level || course.level || "").trim();
      return selectedLevel === "All"
        ? true
        : normalizeText(levelValue) === normalizeText(selectedLevel);
    });
  }, [programmeCourses, selectedLevel]);

  const exportToExcel = () => {
    const exportRows = filteredResults.map((r, index) => ({
      No: index + 1,
      "Full Name": safeText(r.fullName),
      "Matric Number": safeText(r.matricNumber),
      Programme: safeText(r.programme),
      Level: safeText(r.level),
      Session: safeText(r.session),
      Semester: safeText(r.semester),
      "Course Code": safeText(r.courseCode),
      "Course Title": safeText(r.courseTitle),
      Score: safeText(r.score),
      Grade: safeText(r.grade),
      Status: safeText(r.status),
      "Entered By": safeText(r.enteredBy),
      "Entered At": formatDateTime(r.enteredAt),
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Results");

    const programmeName =
      selectedProgramme && selectedProgramme !== "All"
        ? selectedProgramme.replace(/[\\/:*?"<>|]/g, "-")
        : "All_Programmes";

    const levelName =
      selectedLevel && selectedLevel !== "All"
        ? `_Level_${selectedLevel.replace(/[\\/:*?"<>|]/g, "-")}`
        : "";

    const courseName =
      selectedCourse && selectedCourse !== "All"
        ? `_Course_${String(selectedCourse).replace(/[\\/:*?"<>|]/g, "-")}`
        : "";

    XLSX.writeFile(
      wb,
      `Results_${programmeName}${levelName}${courseName}.xlsx`,
    );
  };

  return (
    <>
      <AdminNavBar />
      <div className="students-page">
        <div className="students-main">
          <div className="page-hero">
            <div>
              <p className="eyebrow">Results Directory</p>
              <h1>View student results</h1>
              <p className="hero-text">
                Search by student name or matric number, filter by programme,
                level, and course, and export the current results list to Excel.
              </p>
            </div>

            <div className="hero-actions">
              <button
                type="button"
                className="export-btn"
                onClick={exportToExcel}
                disabled={filteredResults.length === 0}
              >
                Export Results to Excel
              </button>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card stat-blue">
              <span className="stat-label">Total Results</span>
              <strong>{totalResults}</strong>
            </div>
            <div className="stat-card stat-green">
              <span className="stat-label">Approved</span>
              <strong>{approvedCount}</strong>
            </div>
            <div className="stat-card stat-pink">
              <span className="stat-label">Provisional</span>
              <strong>{provisionalCount}</strong>
            </div>
            <div className="stat-card stat-purple">
              <span className="stat-label">Average Score</span>
              <strong>{averageScore}</strong>
            </div>
          </div>

          <div className="filter-panel">
            <div className="filter-group search-group">
              <label htmlFor="search">
                Search by student name, matric no, or course
              </label>
              <input
                id="search"
                type="text"
                placeholder="Type a name, matric number, or course code"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="filter-group programme-group">
              <label htmlFor="programme">Programme</label>
              <select
                id="programme"
                value={selectedProgramme}
                onChange={(e) => {
                  setSelectedProgramme(e.target.value);
                  setSelectedLevel("All");
                  setSelectedCourse("All");
                }}
              >
                <option value="All">All Programmes</option>
                {PROGRAMME_OPTIONS.map((programme, index) => (
                  <option key={`${programme}-${index}`} value={programme}>
                    {programme}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group level-group">
              <label htmlFor="level">Level</label>
              <select
                id="level"
                value={selectedLevel}
                onChange={(e) => {
                  setSelectedLevel(e.target.value);
                  setSelectedCourse("All");
                }}
                disabled={selectedProgramme === "All"}
              >
                <option value="All">
                  {selectedProgramme === "All"
                    ? "Select Programme First"
                    : "All Levels"}
                </option>
                {availableLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group course-group">
              <label htmlFor="course">Course</label>
              <select
                id="course"
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                disabled={
                  loadingCourses ||
                  selectedProgramme === "All" ||
                  selectedLevel === "All"
                }
              >
                <option value="All">
                  {loadingCourses ? "Loading courses..." : "All Courses"}
                </option>
                {availableCourses.map((course) => (
                  <option key={course.CourseID} value={course.CourseID}>
                    {course.CourseCode} - {course.CourseTitle}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {pageError ? <p className="page-error">{pageError}</p> : null}

          <div className="results-bar">
            <span>
              Showing <strong>{filteredResults.length}</strong> result(s)
            </span>
            <span className="results-tag">
              {selectedProgramme === "All"
                ? "All Programmes"
                : selectedProgramme}
              {" • "}
              {selectedLevel === "All" ? "All Levels" : selectedLevel}
              {" • "}
              {selectedCourse === "All" ? "All Courses" : selectedCourse}
            </span>
          </div>

          {loading ? (
            <div className="loading-wrap">
              <div className="loading-spinner" />
              <p>Loading results...</p>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="empty-state">
              <h3>No results found</h3>
              <p>
                There are no results for the selected programme, level, and
                course yet. Try another filter combination.
              </p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Student</th>
                    <th>Matric No</th>
                    <th>Programme</th>
                    <th>Level</th>
                    <th>Session</th>
                    <th>Semester</th>
                    <th>Course</th>
                    <th>Score</th>
                    <th>Grade</th>
                    <th>Status</th>
                    <th>Entered By</th>
                    <th>Entered At</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((r, index) => (
                    <tr
                      key={
                        r.ResultID ||
                        `${r.matricNumber}-${r.courseCode}-${index}`
                      }
                    >
                      <td>{index + 1}</td>
                      <td>{safeText(r.fullName)}</td>
                      <td>{safeText(r.matricNumber)}</td>
                      <td>{safeText(r.programme)}</td>
                      <td>{safeText(r.level)}</td>
                      <td>{safeText(r.session)}</td>
                      <td>{safeText(r.semester)}</td>
                      <td>
                        <strong>{safeText(r.courseCode)}</strong>
                        <br />
                        <span>{safeText(r.courseTitle)}</span>
                      </td>
                      <td>{safeText(r.score)}</td>
                      <td>{safeText(r.grade)}</td>
                      <td>
                        <span
                          className={`status-pill ${
                            normalizeText(r.status) === "approved"
                              ? "status-approved"
                              : "status-provisional"
                          }`}
                        >
                          {safeText(r.status, "Provisional")}
                        </span>
                      </td>
                      <td>{safeText(r.enteredBy)}</td>
                      <td>{formatDateTime(r.enteredAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
